<template>
    <div class="flex flex-col h-full">
        <span class="font-bold text-xl pl-6 pt-4">Kontoavstemming</span>
        <div class="flex gap-6 flex-1 min-h-0 px-6 py-6 ">
            <div class="w-full flex flex-col min-w-0">
                <div class="flex gap-3">
                    <UFormField label="Konto">
                        <transaction-filter-account
                            :account-no="selectedAccountNo"
                            @on-account-no-selected="onAccountSelected"
                        />
                    </UFormField>
                    <UFormField label="År">
                        <USelectMenu
                            v-model="selectedYear"
                            :items="years"
                        />
                    </UFormField>
                    <UFormField label="Måned">
                        <USelectMenu
                            v-model="selectedMonth"
                            :items="months"
                            value-key="value"
                            label-key="label"
                        />
                    </UFormField>
                </div>
                <template v-if="selectedLedgerAccount">
                    <div class="flex justify-between mt-6">
                        <span class="font-bold">{{ selectedAccountNo }} {{ accountName }}</span>
                        <UButton
                            variant="outline"
                            color="neutral"
                            @click="onAddTransaction"
                        >
                            Legg til bilag <img alt="OpenInPanelIcon" :src="isDark ? '/OpenInPanelDark.svg' : '/OpenInPanel.svg'" >
                        </UButton>
                    </div>
                    <ag-grid-vue
                        class="mt-3 grow ring-accented ring-1 rounded-lg"
                        :theme="agTheme"
                        :datasource="tlDatasource"
                        row-model-type="infinite"
                        :column-defs="colDefs"
                        :grid-options="gridOptions"
                        :pinned-top-row-data="pinnedTopRowData"
                        :pinned-bottom-row-data="pinnedBottomRowData"
                    />
                </template>
                <div v-else-if="selectedAccountNo && !loading" class="mt-6 text-muted">
                    Ingen saldo for valgt konto i perioden.
                </div>
            </div>
            <div class="w-full flex flex-col min-h-0 min-w-0">
                <UFormField label="Vedlegg">
                    <div v-if="attachedDocuments[0]" class="flex items-center gap-2 text-sm">
                        <span class="truncate max-w-[200px]">{{ attachedDocuments[0].filename }}</span>
                        <UButton
                            icon="i-ph-x"
                            size="xs"
                            variant="ghost"
                            color="neutral"
                            @click="attachedDocuments = []"
                        />
                    </div>
                    <UFileUpload
                        v-else
                        v-model="pendingFile"
                        label="Dra og slipp fil her"
                        :ui="{ avatar: 'hidden', file: 'bg-default' }"
                        layout="list"
                    >
                        <template #actions>
                            <UButton
                                icon="i-ph-upload"
                                label="Velg fil"
                                variant="outline"
                                color="neutral"
                            />
                        </template>
                    </UFileUpload>
                </UFormField>
                <DocumentPreview
                    v-if="attachedDocuments[0]"
                    :document-meta-uuid="attachedDocuments[0].uuid"
                    class="flex-1 min-h-0"
                />
            </div>
        </div>
        <div class="flex p-8 gap-8 mt-auto bg-default border-muted border-t-1 ">
            <div class="w-full gap-4 flex flex-col text-sm">
                <div class="flex justify-between mr-2">
                    <span>Utgående saldo i regnskapet</span>
                    <span>{{ selectedLedgerAccount ? formatCurrency(selectedLedgerAccount.outgoingBalance) : '-' }}</span>
                </div>
                <div class="flex justify-between items-center">
                    <span>Utgående saldo fra kontoutskrift</span>
                    <UInput
                        v-maska="bankAmountMaskOptions"
                        :model-value="bankStatementDisplay"
                        class="w-40"
                        :ui="{ base: 'text-right text-[length:inherit]' }"
                        @focus="(e: FocusEvent) => (e.target as HTMLInputElement).select()"
                        @blur="onBankAmountBlur"
                        @keydown="(e: KeyboardEvent) => { e.key.length === 1 && !/[\d.,\-]/.test(e.key) && !e.ctrlKey && !e.metaKey && e.preventDefault() }"
                    />
                </div>
                <div class="flex justify-between">
                    <span>Avvik</span>
                    <span :class="hasDeviation ? 'text-red-500' : 'text-green-600'">
                        {{ formatCurrency(deviation) }}
                    </span>
                </div>
            </div>
            <div class="w-full h-full">
                <UFormField label="Kommentar">
                    <UTextarea v-model="comment" class="w-full h-full"/>
                </UFormField>
            </div>
            <div class="mt-auto flex gap-2 text-nowrap">
                <UButton
                    color="neutral"
                    variant="outline"
                    class="flex justify-center w-[120px]"
                    @click="onCancel"
                >
                    Avbryt
                </UButton>
                <UTooltip :delay-duration="0" :text="deviation !== 0 && comment.length === 0 ? 'Skriv kommentar for å godkjenne avvik' : ''">
                    <UButton
                        :disabled="deviation !== 0 && comment.length === 0"
                        :loading="saving"
                        @click="onSave"
                    >
                        Godkjenn avstemming
                    </UButton>
                </UTooltip>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">

import {computed, inject, onMounted, ref, Ref, shallowRef, watch} from 'vue'
import {vMaska} from 'maska/vue'
import {getDocumentMeta} from '@/services/infrastructure/storage.service'
import type {DocumentMeta} from '@/types/transaction/DocumentMeta'
import {fileUpload} from '@/services/document.service'
import DocumentPreview from '@/document/DocumentPreview.vue'
import {AgGridVue} from 'ag-grid-vue3'
import {ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, IDatasource, IGetRowsParams, RowClassParams} from 'ag-grid-community'
import {reconciliationAgTheme as agTheme} from '@/agUtils/ag-theme.service'
import {globalDefaultColDef} from '@/agUtils'
import {formatCurrency, formattedDate} from '@/services/formatter.service'
import {getGeneralLedger} from '@/genleg/ledger.service'
import {getTransactionLines} from '@/transaction/transaction.service'
import {toQueryRequest} from '@/query/AgGridAdapter'
import {Filters} from '@/query/Filters'
import {findAccountByNo} from '@/bookkeeping-account/bookkeepingAccount.service'
import {LedgerAccount} from '@/types/reports/LedgerAccount'
import {Ledger} from '@/types/reports/Ledger'
import {type GeneralLedgerColumn} from '@/genleg/GeneralLedgerColumn'
import TransactionFilterAccount from '@/transaction/TransactionFilterAccount.vue'
import GenLegTransNameCell from '@/genleg/GenLegTransNameCell.vue'
import {format, startOfMonth, endOfMonth} from 'date-fns'
import {nb} from 'date-fns/locale'
import {notifySuccess, notifyWarning, notifyError} from '@/services/infrastructure/notification.service'
import {getReconciliationForPeriod, saveReconciliation, updateReconciliation} from './accountReconciliation.service'
import {useTransactionCreator, newEntryLine} from '@/transaction/create-transaction/TransactionCreator.service'
import {CREATE_TRANSACTION} from '@/transaction/create-transaction'
import {usePanelManager} from '@tritt/multipanel'
import {v4 as uuidv4} from 'uuid'
import {isDark} from '@/theme/theme.service'

const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<string> | undefined = inject('panelPathSegment')

const currentYear = new Date().getFullYear()
const currentMonth = new Date().getMonth() + 1

const years = Array.from({length: 5}, (_, i) => currentYear - i)

const selectedYear = ref<number>(currentYear)
const selectedMonth = ref<number | null>(null)

const months = computed(() => {
    const limit = selectedYear.value === currentYear ? currentMonth : 12
    return [
        {value: null, label: 'Alle måneder'},
        ...Array.from({length: limit}, (_, i) => {
            const date = new Date(selectedYear.value, i, 1)
            return {
                value: i + 1,
                label: format(date, 'MMMM', {locale: nb}),
            }
        }),
    ]
})

// ── Account selection ────────────────────────────────────────────────────────

const selectedAccountNo = ref<string>('1920')
const loading = ref(false)
const ledger = ref<Ledger>()
const gridApi = shallowRef<GridApi>()

const selectedDateRange = computed(() => {
    if (selectedMonth.value !== null) {
        const d = new Date(selectedYear.value, selectedMonth.value - 1, 1)
        return {
            from: format(startOfMonth(d), 'yyyy-MM-dd'),
            to: format(endOfMonth(d), 'yyyy-MM-dd'),
        }
    }
    return {
        from: format(new Date(selectedYear.value, 0, 1), 'yyyy-MM-dd'),
        to: format(new Date(selectedYear.value, 11, 31), 'yyyy-MM-dd'),
    }
})

const selectedLedgerAccount = computed<LedgerAccount | undefined>(() => {
    if (!selectedAccountNo.value || !ledger.value) return undefined
    return ledger.value.ledgerAccounts.find(la => la.accountNo === selectedAccountNo.value)
})

const accountName = computed(() => {
    if (!selectedAccountNo.value) return ''
    return findAccountByNo(selectedAccountNo.value)?.name ?? ''
})

const onAccountSelected = async (accountNo: string | undefined) => {
    selectedAccountNo.value = accountNo ?? ''
    if (!accountNo) {
        ledger.value = undefined
        return
    }
    await fetchLedger()
}

const fetchLedger = async () => {
    if (!selectedAccountNo.value) return
    loading.value = true
    ledger.value = await getGeneralLedger({
        fromDate: selectedDateRange.value.from,
        toDate: selectedDateRange.value.to,
        accountNo: selectedAccountNo.value,
    })
    loading.value = false
}

// ── Reconciliation state ────────────────────────────────────────────────────

const existingUuid = ref<string | null>(null)
const bankStatementAmountInput = ref<string>('')
const bankStatementDisplay = ref<string>('')
const comment = ref<string>('')
const saving = ref(false)

const setBankStatementAmount = (rawNumStr: string) => {
    bankStatementAmountInput.value = rawNumStr
    const num = parseFloat(rawNumStr)
    bankStatementDisplay.value = rawNumStr && !isNaN(num) ? formatCurrency(num) : ''
}

const bankAmountMaskOptions = {
    number: { locale: 'no', fraction: 2, unsigned: false },
    onMaska: (detail: { masked: string; unmasked: string }) => {
        bankStatementDisplay.value = detail.masked
        bankStatementAmountInput.value = detail.unmasked
    },
}

const onBankAmountBlur = () => {
    const num = parseFloat(bankStatementAmountInput.value)
    if (!isNaN(num)) bankStatementDisplay.value = formatCurrency(num)
}

// Saved/loaded values for cancel reset
const savedBankStatementAmount = ref<number>(0)
const savedComment = ref<string>('')

const attachedDocuments = ref<DocumentMeta[]>([])
const savedAttachedDocuments = ref<DocumentMeta[]>([])
const pendingFile = ref<File | null>(null)

watch(pendingFile, async (file) => {
    if (!file) return
    const doc = await fileUpload(file)
    attachedDocuments.value = [doc]
    pendingFile.value = null
})

// Period-derived computed values
const periodYear = computed(() => selectedYear.value)
const periodMonth = computed(() => selectedMonth.value ?? currentMonth)

// GL outgoing balance in NOK (the field is already in NOK from the ledger)
const glOutgoingBalanceNOK = computed(() => selectedLedgerAccount.value?.outgoingBalance ?? 0)

// deviation in NOK
const deviation = computed(() => (parseFloat(bankStatementAmountInput.value) || 0) - glOutgoingBalanceNOK.value)

// hasDeviation
const hasDeviation = computed(() => deviation.value !== 0)

const loadReconciliation = async () => {
    if (!selectedAccountNo.value) return
    const existing = await getReconciliationForPeriod(
        selectedAccountNo.value,
        'MONTH',
        periodYear.value,
        periodMonth.value
    )
    if (existing) {
        existingUuid.value = existing.uuid
        const amountNOK = existing.bankBalance / 100
        setBankStatementAmount(String(amountNOK))
        savedBankStatementAmount.value = amountNOK
        comment.value = existing.comment ?? ''
        savedComment.value = existing.comment ?? ''
        const doc = existing.documentMetaUuid ? (await getDocumentMeta(existing.documentMetaUuid) ?? null) : null
        attachedDocuments.value = doc ? [doc] : []
        savedAttachedDocuments.value = doc ? [doc] : []
    } else {
        existingUuid.value = null
        setBankStatementAmount('')
        savedBankStatementAmount.value = 0
        comment.value = ''
        savedComment.value = ''
        attachedDocuments.value = []
        savedAttachedDocuments.value = []
    }
}

const onSave = async () => {
    if (!selectedAccountNo.value) return
    saving.value = true
    try {
        const centimalAmount = Math.round((parseFloat(bankStatementAmountInput.value) || 0) * 100)
        if (existingUuid.value) {
            const updated = await updateReconciliation(selectedAccountNo.value, existingUuid.value, {
                bankBalance: centimalAmount,
                comment: comment.value || null,
                documentMetaUuid: attachedDocuments.value[0]?.uuid ?? null,
            })
            if (updated) {
                savedBankStatementAmount.value = parseFloat(bankStatementAmountInput.value) || 0
                savedComment.value = comment.value
                savedAttachedDocuments.value = [...attachedDocuments.value]
                notifySuccess({title: 'Lagret', description: 'Kontoavstemming ble oppdatert.'})
            }
        } else {
            const saved = await saveReconciliation(selectedAccountNo.value, {
                periodType: 'MONTH',
                periodYear: periodYear.value,
                periodMonth: periodMonth.value,
                bankBalance: centimalAmount,
                comment: comment.value || null,
                documentMetaUuid: attachedDocuments.value[0]?.uuid ?? null,
            })
            if (saved) {
                existingUuid.value = saved.uuid
                savedBankStatementAmount.value = parseFloat(bankStatementAmountInput.value) || 0
                savedComment.value = comment.value
                savedAttachedDocuments.value = [...attachedDocuments.value]
                notifySuccess({title: 'Lagret', description: 'Kontoavstemming ble lagret.'})
            }
        }
    } catch (e: unknown) {
        const axiosErr = e as { isAxiosError?: boolean; response?: { status?: number } }
        if (axiosErr?.isAxiosError && axiosErr.response?.status === 409) {
            notifyWarning({title: 'Konflikt', description: 'En avstemming for denne perioden finnes allerede.'})
        } else {
            notifyError({title: 'Feil', description: 'Klarte ikke å lagre kontoavstemming.'})
        }
    } finally {
        saving.value = false
    }
}

const onCancel = () => {
    setBankStatementAmount(savedBankStatementAmount.value ? String(savedBankStatementAmount.value) : '')
    comment.value = savedComment.value
    attachedDocuments.value = [...savedAttachedDocuments.value]
}

// ── Add transaction ──────────────────────────────────────────────────────────

const { transactionEntryLines } = useTransactionCreator()
const { setPanelPath } = usePanelManager()

const onAddTransaction = () => {
    const year = selectedYear.value
    const month = selectedMonth.value ?? currentMonth
    const prefillDate = endOfMonth(new Date(year, month - 1, 1))
    const absDeviation = Math.abs(deviation.value)
    const line = newEntryLine(0)
    line.entryUuid = uuidv4()
    line.date = prefillDate
    line.amount = absDeviation
    if (deviation.value > 0) {
        line.debitAccount = selectedAccountNo.value
    } else if (deviation.value < 0) {
        line.creditAccount = selectedAccountNo.value
    } else {
        line.debitAccount = selectedAccountNo.value
    }
    transactionEntryLines.value = [line]
    setPanelPath({
        ...CREATE_TRANSACTION,
        queryValues: {},
        panelId: panelId?.value ? panelId.value + 1 : 1,
    })
}

onMounted(() => {
    fetchLedger()
    loadReconciliation()
})

// Load reconciliation when account or period changes
watch([selectedAccountNo, selectedYear, selectedMonth], () => {
    if (selectedAccountNo.value) loadReconciliation()
})

// ── AG Grid datasource ──────────────────────────────────────────────────────

const tlDatasource = computed<IDatasource>(() => ({
    rowCount: selectedLedgerAccount.value?.numTransactionLines,
    async getRows(params: IGetRowsParams) {
        if (!selectedAccountNo.value) return
        const queryRequest = toQueryRequest<GeneralLedgerColumn>(params as any)
        queryRequest.filters = {
            date: Filters.and([Filters.date(selectedDateRange.value.from).onOrAfter(), Filters.date(selectedDateRange.value.to).onOrBefore()]),
            accountNo: Filters.text(selectedAccountNo.value).equals(),
        }
        const tls = await getTransactionLines(queryRequest)
        params.successCallback(tls, selectedLedgerAccount.value?.numTransactionLines)
    }
}))

// ── Pinned rows ─────────────────────────────────────────────────────────────

const pinnedTopRowData = computed(() => {
    if (!selectedLedgerAccount.value) return []
    return [{
        date: selectedDateRange.value.from,
        uuid: 'incoming-balance',
        description: 'Inngående saldo',
        type: 'incoming-balance',
        amount: selectedLedgerAccount.value.incomingBalance,
    }]
})

const pinnedBottomRowData = computed(() => {
    if (!selectedLedgerAccount.value) return []
    return [
        {
            date: selectedDateRange.value.to,
            uuid: 'outgoing-balance',
            description: 'Utgående saldo',
            type: 'outgoing-balance',
            amount: selectedLedgerAccount.value.outgoingBalance,
        },
        {
            uuid: 'sum-changes',
            description: 'Endring i perioden',
            type: 'sum-changes',
            amount: selectedLedgerAccount.value.movement,
        }
    ]
})

// ── Grid config ─────────────────────────────────────────────────────────────

const gridOptions: GridOptions = {
    getRowId: ({data}) => data.uuid,
    onGridReady(event: GridReadyEvent) {
        gridApi.value = event.api
    },
    enableCellTextSelection: true,
    suppressCellFocus: true,
    getRowClass: (params: RowClassParams) => {
        if (['incoming-balance', 'outgoing-balance', 'sum-changes'].includes(params.data?.type)) {
            return '!bg-slate-100 dark:!bg-base-200'
        }
    },
    defaultColDef: {
        ...globalDefaultColDef,
        sortable: false,
        filter: false,
    },
    context: {
        panelId,
        panelPathSegment,
    },
}

const colDefs: ColDef[] = [
    {
        headerName: 'Dato',
        field: 'date',
        flex: 1,
        cellRenderer: ({value}: ICellRendererParams) => formattedDate(value),
        sortable: true,
        sort: 'asc',
    },
    {
        headerName: 'Bilag',
        field: 'transactionName',
        flex: 1,
        cellRenderer: GenLegTransNameCell,
    },
    {
        headerName: 'Beskrivelse',
        field: 'description',
        flex: 2,
    },
    {
        headerName: 'Beløp',
        field: 'amount',
        flex: 1,
        resizable: false,
        type: 'rightAligned',
        cellClassRules: {
            'font-bold': ({data}) => ['incoming-balance', 'outgoing-balance', 'sum-changes'].includes(data?.type),
        },
        cellRenderer: ({value}: ICellRendererParams) => formatCurrency(value),
        sortable: true,
    }
]

// Re-fetch ledger when period changes
watch([selectedYear, selectedMonth], () => {
    if (selectedAccountNo.value) fetchLedger()
})

// Refresh grid when ledger account changes
watch(selectedLedgerAccount, () => {
    gridApi.value?.setGridOption('datasource', tlDatasource.value)
})

</script>

<style scoped>

</style>
